import { apiClient, ApiResponse } from './apiClient';

export interface StaticPage {
  id: number;
  title_ar: string;
  title_en: string;
  content_ar: string;
  content_en: string;
  slug: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StaticPageFilters {
  search?: string;
  is_active?: boolean;
  page?: number;
  limit?: number;
}

export interface StaticPageListResponse {
  pages: StaticPage[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateStaticPageRequest {
  title_ar: string;
  title_en: string;
  content_ar: string;
  content_en: string;
  slug: string;
}

export interface UpdateStaticPageRequest {
  title_ar?: string;
  title_en?: string;
  content_ar?: string;
  content_en?: string;
  slug?: string;
  is_active?: boolean;
}

export interface StaticPageStats {
  total_pages: number;
  active_pages: number;
  inactive_pages: number;
}

export class StaticPagesService {
  private baseUrl = '/admin/pages';

  // Get all static pages with filters
  async getStaticPages(filters: StaticPageFilters = {}): Promise<ApiResponse<StaticPageListResponse>> {
    try {
      console.log('🔧 StaticPagesService.getStaticPages called:', { filters });
      
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString());
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const queryString = params.toString();
      const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
      
      console.log('🔧 Making GET request to:', url);
      console.log('🔧 Query parameters:', Object.fromEntries(params));
      
      const response = await apiClient.get<StaticPage[]>(url);
      
      console.log('🔧 StaticPagesService.getStaticPages response:', response);
      console.log('🔧 Response success:', response.success);
      console.log('🔧 Response data type:', typeof response.data);
      console.log('🔧 Response data:', response.data);
      
      if (response.success && response.data) {
        // Handle different response formats
        let pages: StaticPage[] = [];
        let total = 0;
        let page = filters.page || 1;
        let limit = filters.limit || 10;
        
        console.log('🔧 Processing response data:', response.data);
        console.log('🔧 Is array?', Array.isArray(response.data));
        console.log('🔧 Has pages property?', !!(response.data as any).pages);
        
        if (Array.isArray(response.data)) {
          // Direct array response
          console.log('🔧 Using direct array format');
          pages = response.data;
          total = pages.length;
        } else if ((response.data as any).pages) {
          // Paginated response
          console.log('🔧 Using paginated format');
          const paginatedData = response.data as any;
          pages = paginatedData.pages || [];
          total = paginatedData.total || pages.length;
          page = paginatedData.page || page;
          limit = paginatedData.limit || limit;
        } else {
          // Try to extract from nested data structure
          console.log('🔧 Trying nested data extraction');
          const nestedData = (response.data as any).data;
          if (Array.isArray(nestedData)) {
            pages = nestedData;
            total = pages.length;
          } else if (nestedData && nestedData.pages) {
            pages = nestedData.pages || [];
            total = nestedData.total || pages.length;
            page = nestedData.page || page;
            limit = nestedData.limit || limit;
          }
        }
        
        console.log('🔧 Final extracted pages:', pages);
        console.log('🔧 Final pages count:', pages.length);
        
        const totalPages = Math.ceil(total / limit);
        
        const listResponse: StaticPageListResponse = {
          pages,
          total,
          page,
          limit,
          totalPages
        };
        
        return {
          success: true,
          data: listResponse,
          message: 'Static pages retrieved successfully'
        };
      }
      
      return {
        success: false,
        data: {
          pages: [],
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0
        },
        message: 'No static pages data received'
      };
    } catch (error: any) {
      console.error('🔧 StaticPagesService.getStaticPages error:', error);
      
      return {
        success: false,
        data: {
          pages: [],
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0
        },
        message: error.message || 'Error fetching static pages'
      };
    }
  }

  // Get single static page by ID
  async getStaticPage(id: number): Promise<ApiResponse<StaticPage>> {
    try {
      console.log('🔧 StaticPagesService.getStaticPage called:', { id });
      
      const response = await apiClient.get<StaticPage>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 StaticPagesService.getStaticPage response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 StaticPagesService.getStaticPage error:', error);
      throw error;
    }
  }

  // Create new static page
  async createStaticPage(data: CreateStaticPageRequest): Promise<ApiResponse<StaticPage>> {
    try {
      console.log('🔧 StaticPagesService.createStaticPage called:', { data });
      
      // Prepare data for API
      const pageData = {
        title_ar: data.title_ar.trim(),
        title_en: data.title_en.trim(),
        content_ar: data.content_ar.trim(),
        content_en: data.content_en.trim(),
        slug: data.slug.trim().toLowerCase().replace(/\s+/g, '-')
      };
      
      console.log('🔧 StaticPagesService.createStaticPage prepared data:', pageData);
      console.log('🔧 Making POST request to:', this.baseUrl);
      console.log('🔧 Request payload:', JSON.stringify(pageData, null, 2));
      
      const response = await apiClient.post<StaticPage>(this.baseUrl, pageData);
      
      console.log('🔧 StaticPagesService.createStaticPage response:', response);
      console.log('🔧 Response status:', response.success);
      console.log('🔧 Response data:', response.data);
      
      return response;
    } catch (error: any) {
      console.error('🔧 StaticPagesService.createStaticPage error:', error);
      console.error('🔧 Error response data:', error.response?.data);
      console.error('🔧 Error response status:', error.response?.status);
      
      let errorMessage = 'Error creating static page';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Handle validation errors array
        const validationErrors = error.response.data.errors.map((err: any) => {
          if (typeof err === 'string') return err;
          if (err.msg && err.path) return `${err.path}: ${err.msg}`;
          return err.msg || err.message || 'Validation error';
        });
        errorMessage = validationErrors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage,
        errors: error.response?.data?.errors || []
      };
    }
  }

  // Update existing static page
  async updateStaticPage(id: number, data: UpdateStaticPageRequest): Promise<ApiResponse<StaticPage>> {
    try {
      console.log('🔧 StaticPagesService.updateStaticPage called:', { id, data });
      
      // Prepare data for API - send complete request body as specified
      const updateData = {
        title_ar: data.title_ar?.trim() || '',
        title_en: data.title_en?.trim() || '',
        content_ar: data.content_ar?.trim() || '',
        content_en: data.content_en?.trim() || ''
      };
      
      console.log('🔧 StaticPagesService.updateStaticPage prepared data (PUT):', updateData);
      console.log('🔧 Making PUT request to:', `${this.baseUrl}/${id}`);
      console.log('🔧 Request payload:', JSON.stringify(updateData, null, 2));
      
      const response = await apiClient.put<StaticPage>(`${this.baseUrl}/${id}`, updateData);
      
      console.log('🔧 StaticPagesService.updateStaticPage response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 StaticPagesService.updateStaticPage error:', error);
      
      let errorMessage = 'Error updating static page';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const validationErrors = error.response.data.errors.map((err: any) => {
          if (typeof err === 'string') return err;
          if (err.msg && err.path) return `${err.path}: ${err.msg}`;
          return err.msg || err.message || 'Validation error';
        });
        errorMessage = validationErrors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage,
        errors: error.response?.data?.errors || []
      };
    }
  }

  // Delete static page
  async deleteStaticPage(id: number): Promise<ApiResponse<void>> {
    try {
      console.log('🔧 StaticPagesService.deleteStaticPage called:', { id });
      
      const response = await apiClient.delete<void>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 StaticPagesService.deleteStaticPage response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 StaticPagesService.deleteStaticPage error:', error);
      throw error;
    }
  }

  // Toggle page active status
  async toggleActive(id: number, is_active: boolean): Promise<ApiResponse<StaticPage>> {
    try {
      console.log('🔧 StaticPagesService.toggleActive called:', { id, is_active });
      
      // First get the current page data, then update with new status
      const currentPage = await this.getStaticPage(id);
      if (!currentPage.success || !currentPage.data) {
        return {
          success: false,
          data: null as any,
          message: 'Could not fetch current page data'
        };
      }
      
      // Update with complete data including new status
      return this.updateStaticPage(id, {
        title_ar: currentPage.data.title_ar,
        title_en: currentPage.data.title_en,
        content_ar: currentPage.data.content_ar,
        content_en: currentPage.data.content_en,
        is_active
      });
    } catch (error: any) {
      console.error('🔧 StaticPagesService.toggleActive error:', error);
      
      return {
        success: false,
        data: null as any,
        message: 'Error updating page status'
      };
    }
  }

  // Get static page statistics (if endpoint exists)
  async getStaticPageStats(): Promise<ApiResponse<StaticPageStats>> {
    try {
      console.log('🔧 StaticPagesService.getStaticPageStats called');
      
      const response = await apiClient.get<StaticPageStats>(`${this.baseUrl}/stats`);
      
      console.log('🔧 StaticPagesService.getStaticPageStats response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 StaticPagesService.getStaticPageStats error:', error);
      throw error;
    }
  }
}

export const staticPagesService = new StaticPagesService();
